"use client";
import { VENUE_TABS } from "@/@core/constants/venue-tabs";
import Services from "@/components/views/venue-details/PlaceDetailsServices";
import VenueDetailTrainerList from "@/components/views/venue-details/card/crew/Crew";
import Discount from "@/components/views/venue-details/card/Discount";
import Campaign from "@/components/views/venue-details/card/Campaign";
import VenueDetailEvents from "@/components/views/venue-details/card/Event";
import { useSite } from "@/lib/hooks/site";
import { parseHtml } from "@/lib/utils";
import PlaceDetailsCompany from "./PlaceDetailsCompany";
import dynamic from "next/dynamic";
import VenueDetailAskQuestion from "@/components/views/venue-details/new-details/ask-question";

const Images = dynamic(
  () => import("@/components/views/venue-details/card/Images"),
);
const Videos = dynamic(
  () => import("@/components/views/venue-details/card/Videos"),
);
const Contact = dynamic(
  () => import("@/components/views/venue-details/card/Contact"),
);
const About = dynamic(
  () => import("@/components/views/venue-details/card/About"),
);
const WorkingHours = dynamic(
  () => import("@/components/views/venue-details/card/WorkingHours"),
);
const Comments = dynamic(
  () => import("@/components/views/venue-details/card/Comments"),
);

interface Props {
  data: any;
  companyData: any;
  links: any;
  comments: Venue.Comment[]
}

export default function VenueContent(props: Props) {
  const { data, links, companyData, comments } = props;
  const site = useSite();

  const header: {
    phone: string;
    title: string;
    discount_title: string;
    id: number;
    banners: { url: string; sort_order: string }[];
    videos: string[];
    location: string;
    tags: { title: string }[];
    logo: string;
    clickAdressLink: string;
  } = data && {
    phone: data.phones?.[0]?.phone,
    title: data.descriptions[0].title,
    discount_title: data.descriptions[0]?.discount_title,
    logo: data.logo,
    id: data.descriptions[0].id,
    location: `${data.district.title}, ${data.city.title}`,
    banners: data.photos,
    videos: data.videos?.map((video: any) => video?.video?.youtube_id),
    tags: [...data.plan.descriptions],
    clickAdressLink: `https://www.google.com/maps/search/${data.map_latitude}, ${data.map_longitude}`,
  };

  const about: {
    title: string;
    description: string;
    address: string;
    addressExplanation: string;
    googleAdressLink: string;
    clickAdressLink: string;
  } = data && {
    title: data.descriptions[0]?.title,
    description: parseHtml(data.descriptions[0].description),
    address: data.descriptions[0].address,
    addressExplanation: data.descriptions[0].address_explanation,
    clickAdressLink: `https://www.google.com/maps/search/${data.map_latitude}, ${data.map_longitude}`,
    googleAdressLink: `https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3060.906345378031!2d${data.map_longitude}!3d${data.map_latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMznCsDUzJzU1LjQiTiAzMsKwNTAnNDIuMCJF!5e0!3m2!1str!2str!4v1691411478492!5m2!1str!2st`,
  };

  const hours = data.hours;

  const visibleLinks = links.filter((link: any) => {
    if (link.key == "company-discounts") {
      return site?.setting?.disabledPages?.company !== 1;
    }

    return true;
  });

  const defaultSortOrder = visibleLinks.map((link: any, index: number) => ({
    key: link.key,
    sort_order: index + 1,
  }));

  const getSortOrder = (key: string) => {
    const realSortOrder = site.tabs
      ? site.tabs.from_site_tabs.find((tab) => tab.type === key)?.sort_order ||
        0
      : 0;
    const defaultOrder =
      defaultSortOrder.find((order: any) => order.key === key)?.sort_order || 0;
    return realSortOrder || defaultOrder;
  };

  const sorted = visibleLinks.sort((a: any, b: any) => {
    const sortOrderA = getSortOrder(a.key);
    const sortOrderB = getSortOrder(b.key);

    if (sortOrderA === sortOrderB) {
      return -1;
    }

    return sortOrderA - sortOrderB;
  });

  const getServices = () => {
    const services = data?.services || [];
    const firstColumnItems = services.slice(0, Math.round(services.length / 3));
    const secondColumnItems = services.slice(
      Math.round(services.length / 3),
      Math.round((services.length * 2) / 3),
    );
    const thirdColumnItems = services.slice(
      Math.round((services.length * 2) / 3),
      services.length,
    );

    return {
      firstServices: firstColumnItems,
      secondServices: secondColumnItems,
      thirdServices: thirdColumnItems,
      totalCount: data.services?.length || 0,
    };
  };

  const renderFunctions = {
    "company-discounts": () => (
      <PlaceDetailsCompany
        phone={header.phone}
        venueTitle={header.title}
        id={header.id}
        datas={companyData}
      />
    ),
    services: () => <Services services={getServices()} key="services" />,
    "ask-question": () => <VenueDetailAskQuestion />,
    crew: () => <VenueDetailTrainerList {...data} key="crew" />,
    comments: () => (
      <Comments
        venueId={data.id}
        sendCommentDatas={{
          title: header.title,
          image: header.logo,
          id: header.id,
        }}
        datas={comments}
        key="comments"
      />
    ),
    discounts: () => (
      <Discount
        company={companyData.data.length}
        datas={data.discounts}
        phone={data?.whatsappPhoneNumber}
        key="discounts"
      />
    ),
    campaigns: () => (
      <Campaign datas={data.campaigns} venue={data} key="campaigns" />
    ),
    events: () => (
      <VenueDetailEvents events={data.events} venue={data} key="events" />
    ),
    photos: () => (
      <Images
        title={data.descriptions[0]?.title}
        datas={data.photos}
        id={data.descriptions[0].id}
        key="photos"
      />
    ),
    videos: () => <Videos datas={data.videos} key="videos" />,
    about: () => <About datas={about} />,
    "working-hours": () => <WorkingHours datas={{ hours }} />,
    contact: () => (
      <Contact
        venue={data}
        id={header.id}
        whatsappPhone={data?.whatsappPhoneNumber}
        datas={about}
      />
    ),
  };

  const sortedComponents = sorted.map((link: any) => {
    // @ts-ignore
    const renderFunction = renderFunctions[link.key];
    return renderFunction && <div key={link.key}>{renderFunction()}</div>;
  });

  return <>{sortedComponents}</>;
}
